import React, { useState } from 'react';
import {
  Shield,
  CheckCircle2,
  AlertTriangle,
  Clock,
  QrCode,
  MapPin,
  Camera,
  Calendar,
  Filter,
  CheckSquare,
  Search,
  ExternalLink,
  ChevronRight,
  Eye,
  Building,
  User,
} from 'lucide-react';
import { PatrolCheckpoint, PatrolScanLog } from '../types';
import { useApp } from '../context/AppContext';

export const SecurityPatrolMonitor: React.FC = () => {
  const { patrolCheckpoints, patrolLogs } = useApp();
  const [selectedZone, setSelectedZone] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'normal' | 'abnormal'>('all');
  const [selectedCheckpoint, setSelectedCheckpoint] = useState<PatrolCheckpoint | null>(null);
  const [selectedScanDetail, setSelectedScanDetail] = useState<PatrolScanLog | null>(null);

  const zones = Array.from(new Set(patrolCheckpoints.map((c) => c.zone)));

  // Filtered Scan Logs
  const filteredLogs = patrolLogs.filter((log) => {
    const matchZone = selectedZone === 'all' || log.checkpoint_zone === selectedZone;
    const matchStatus = statusFilter === 'all' || log.status === statusFilter;
    return matchZone && matchStatus;
  });

  const abnormalLogs = patrolLogs.filter((log) => log.status === 'abnormal');
  const totalScansToday = patrolLogs.length;

  return (
    <div className="space-y-6">
      {/* Top Overview KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        {/* Total Checkpoints */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between">
          <div>
            <span className="text-xs font-medium text-slate-500 block">จุดตรวจ QR Checkpoints</span>
            <div className="text-2xl font-bold text-slate-800 mt-1">
              {patrolCheckpoints.length} <span className="text-xs font-normal text-slate-500">จุดทั่วหมู่บ้าน</span>
            </div>
          </div>
          <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 border border-blue-200 flex items-center justify-center">
            <QrCode className="w-5 h-5" />
          </div>
        </div>

        {/* Total Scans Today */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between">
          <div>
            <span className="text-xs font-medium text-slate-500 block">การสแกนตรวจรอบวันนี้</span>
            <div className="text-2xl font-bold text-slate-800 mt-1">
              {totalScansToday} <span className="text-xs font-normal text-slate-500">ครั้ง</span>
            </div>
          </div>
          <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-200 flex items-center justify-center">
            <Clock className="w-5 h-5" />
          </div>
        </div>

        {/* Normal Status */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between">
          <div>
            <span className="text-xs font-medium text-slate-500 block">จุดตรวจสถานะปกติ</span>
            <div className="text-2xl font-bold text-emerald-600 mt-1">
              {patrolLogs.filter((l) => l.status === 'normal').length}
            </div>
          </div>
          <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-200 flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5" />
          </div>
        </div>

        {/* Anomalies Detected */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-xs flex items-center justify-between">
          <div>
            <span className="text-xs font-medium text-slate-500 block">เหตุผิดปกติ / แจ้งซ่อม</span>
            <div className="text-2xl font-bold text-amber-600 mt-1">{abnormalLogs.length}</div>
          </div>
          <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-600 border border-amber-200 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Abnormal Alert Banner (if any) */}
      {abnormalLogs.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 sm:p-5 space-y-3 animate-in fade-in">
          <div className="flex items-center gap-2 text-amber-900 font-bold text-sm">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            รายงานเหตุการณ์ผิดปกติที่ต้องติดตาม ({abnormalLogs.length} รายการ)
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {abnormalLogs.map((item) => (
              <div
                key={item.id}
                onClick={() => setSelectedScanDetail(item)}
                className="bg-white p-3.5 rounded-xl border border-amber-200 shadow-2xs hover:shadow-xs cursor-pointer transition-all flex items-start gap-3"
              >
                {item.photo_url && (
                  <img
                    src={item.photo_url}
                    alt="Evidence"
                    className="w-16 h-16 rounded-lg object-cover border border-slate-200 shrink-0"
                  />
                )}
                <div className="space-y-1 flex-1 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-800">{item.checkpoint_code}</span>
                    <span className="text-[11px] text-slate-400 font-mono">{item.scanned_time}</span>
                  </div>
                  <p className="font-semibold text-slate-700">{item.checkpoint_name}</p>
                  <p className="text-amber-800 line-clamp-1 italic">"{item.note || 'ตรวจพบข้อบกพร่อง'}"</p>
                  <div className="text-[10px] text-slate-500">โดย: {item.officer_name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Checkpoints Grid & Live Scan Feed */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left 2 Cols: 7 Checkpoints Map/List */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-700" />
              <h3 className="font-bold text-slate-800 text-sm sm:text-base">
                ผังจุดตรวจลาดตระเวน 7 จุด (QR Security Network)
              </h3>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {patrolCheckpoints.map((cp) => {
              const lastScan = patrolLogs.find((l) => l.checkpoint_id === cp.id);
              return (
                <div
                  key={cp.id}
                  onClick={() => setSelectedCheckpoint(cp)}
                  className="bg-white rounded-2xl border border-slate-200 p-4 shadow-xs hover:border-blue-400 cursor-pointer transition-all space-y-3 group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-xl bg-blue-100 text-blue-700 font-bold font-mono text-xs flex items-center justify-center">
                        {cp.sequence}
                      </div>
                      <div>
                        <span className="font-mono text-xs font-bold text-blue-700 block">{cp.code}</span>
                        <h4 className="font-bold text-slate-800 text-xs line-clamp-1">{cp.name}</h4>
                      </div>
                    </div>

                    {lastScan ? (
                      lastScan.status === 'normal' ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                          <CheckCircle2 className="w-2.5 h-2.5" /> ปกติ
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1">
                          <AlertTriangle className="w-2.5 h-2.5" /> มีรายงาน
                        </span>
                      )
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[10px] bg-slate-100 text-slate-500">
                        รอยืนยันรอบ
                      </span>
                    )}
                  </div>

                  <p className="text-xs text-slate-500 line-clamp-2">{cp.description}</p>

                  <div className="flex items-center justify-between text-[11px] pt-2 border-t border-slate-100 text-slate-500">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-slate-400" /> {cp.zone}
                    </span>
                    <span className="text-blue-600 group-hover:translate-x-0.5 transition-transform flex items-center font-medium">
                      ดูหัวข้อตรวจ <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right 1 Col: Real-time Scan Feed */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-700" /> บันทึกการเดินตรวจล่าสุด
            </h3>
            <span className="text-xs text-slate-500">{filteredLogs.length} รายการ</span>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-xs space-y-3 max-h-[600px] overflow-y-auto">
            {filteredLogs.length > 0 ? (
              filteredLogs.map((log) => (
                <div
                  key={log.id}
                  onClick={() => setSelectedScanDetail(log)}
                  className="p-3 rounded-xl border border-slate-100 hover:border-slate-300 hover:bg-slate-50/50 cursor-pointer transition-all space-y-2 text-xs"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-800">{log.checkpoint_code}</span>
                    <span className="font-mono text-[11px] text-slate-500">{log.scanned_time}</span>
                  </div>
                  <p className="text-slate-700 font-medium">{log.checkpoint_name}</p>

                  <div className="flex items-center justify-between pt-1 text-[11px]">
                    <span className="text-slate-500 flex items-center gap-1">
                      <User className="w-3 h-3 text-slate-400" /> {log.officer_name}
                    </span>
                    {log.status === 'normal' ? (
                      <span className="text-emerald-700 font-medium flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> ผ่านการตรวจ
                      </span>
                    ) : (
                      <span className="text-amber-700 font-medium flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> ข้อบกพร่อง
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="py-8 text-center text-slate-400 text-xs">ไม่มีรายการสแกนในตัวกรองนี้</div>
            )}
          </div>
        </div>
      </div>

      {/* Checkpoint Detail Modal */}
      {selectedCheckpoint && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-slate-100 overflow-hidden my-6 animate-in fade-in zoom-in-95">
            <div className="p-5 bg-slate-900 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <QrCode className="w-5 h-5 text-blue-400" />
                <div>
                  <h3 className="font-bold text-base">{selectedCheckpoint.name}</h3>
                  <p className="text-xs text-slate-400">
                    รหัสจุดตรวจ: {selectedCheckpoint.code} • {selectedCheckpoint.zone}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedCheckpoint(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs">
              {selectedCheckpoint.image_url && (
                <img
                  src={selectedCheckpoint.image_url}
                  alt={selectedCheckpoint.name}
                  className="w-full h-40 object-cover rounded-xl border border-slate-200 shadow-inner"
                />
              )}

              <div>
                <span className="font-bold text-slate-700 block mb-1">คำอธิบายจุดตรวจ & วัตถุประสงค์:</span>
                <p className="text-slate-600 leading-relaxed">{selectedCheckpoint.description}</p>
              </div>

              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                <span className="font-bold text-slate-700 flex items-center gap-1.5">
                  <CheckSquare className="w-4 h-4 text-blue-600" /> รายการตรวจสอบความปลอดภัย (Checklist Items):
                </span>
                <ul className="space-y-1.5 pl-2">
                  {selectedCheckpoint.checklist_items.map((item, idx) => (
                    <li key={idx} className="text-slate-600 flex items-start gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-3 bg-blue-50/60 rounded-xl border border-blue-100 flex items-center justify-between text-blue-900 font-mono text-[11px]">
                <span>Payload QR: {selectedCheckpoint.qr_data}</span>
                <span className="px-2 py-0.5 bg-blue-200/60 rounded text-blue-800">ลำดับที่ {selectedCheckpoint.sequence}</span>
              </div>
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end">
              <button
                onClick={() => setSelectedCheckpoint(null)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-semibold"
              >
                ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scan Log Detail Modal */}
      {selectedScanDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-slate-100 overflow-hidden my-6 animate-in fade-in zoom-in-95">
            <div className="p-5 bg-slate-900 text-white flex items-center justify-between">
              <h3 className="font-bold text-base">รายละเอียดผลการตรวจลาดตระเวน</h3>
              <button
                onClick={() => setSelectedScanDetail(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs">
              <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                <span className="font-bold text-slate-800 text-sm">{selectedScanDetail.checkpoint_name}</span>
                <span className="font-mono text-slate-500">{selectedScanDetail.scanned_time}</span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-slate-600">
                <div>
                  ผู้ตรวจ: <strong>{selectedScanDetail.officer_name}</strong>
                </div>
                <div>
                  รอบการตรวจ: <strong>{selectedScanDetail.round_time_slot}</strong>
                </div>
              </div>

              {selectedScanDetail.photo_url && (
                <div>
                  <span className="font-semibold text-slate-700 block mb-1">ภาพถ่ายขณะตรวจ:</span>
                  <img
                    src={selectedScanDetail.photo_url}
                    alt="Scan Evidence"
                    className="w-full h-44 object-cover rounded-xl border border-slate-200"
                  />
                </div>
              )}

              {selectedScanDetail.note && (
                <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 text-amber-900">
                  <span className="font-bold block mb-1">หมายเหตุรายงาน:</span>
                  <p>{selectedScanDetail.note}</p>
                </div>
              )}

              <div className="space-y-1.5">
                <span className="font-bold text-slate-700 block">ผลการตรวจเช็คลิสต์:</span>
                {Object.entries(selectedScanDetail.checklist_results).map(([k, v], idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                    <span className="text-slate-700">{k}</span>
                    {v ? (
                      <span className="text-emerald-700 font-bold flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> ผ่าน
                      </span>
                    ) : (
                      <span className="text-rose-700 font-bold flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5" /> ไม่ผ่าน
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end">
              <button
                onClick={() => setSelectedScanDetail(null)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-semibold"
              >
                ปิด
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
